import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Users,
  FileText,
  Globe,
  BookOpen,
  HelpCircle,
  MessageSquare,
} from 'lucide-react';

const Home = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="container mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Создавайте FAQ и Manual для ваших сайтов
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Простой и удобный инструмент для создания FAQ и мануалов с вложенными структурами
            </p>
            {user ? (
              <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 justify-center">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
                >
                  <HelpCircle className="h-5 w-5 mr-2" />
                  Создать FAQ
                </Link>
                <Link
                  to="/manual/new"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-600 transition-colors"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  Создать Manual
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
                >
                  Начать бесплатно
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-600 transition-colors"
                >
                  Войти
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Возможности платформы
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Все необходимое для создания профессиональных FAQ
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                Простое создание
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Интуитивный интерфейс для быстрого создания FAQ с категориями и вопросами
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                Гибкая настройка
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Настройте внешний вид, цвета и структуру вашего FAQ под ваш бренд
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                Публичные ссылки
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Получите уникальную ссылку для встраивания FAQ на любой сайт
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Как это работает
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Создайте ваш FAQ за несколько простых шагов
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Регистрация', desc: 'Создайте аккаунт и войдите в систему' },
              { step: '2', title: 'Создание FAQ/Manual', desc: 'Добавьте название и описание вашего FAQ/Manual' },
              { step: '3', title: 'Наполнение', desc: 'Создайте категории и добавьте вопросы с ответами' },
              { step: '4', title: 'Публикация', desc: 'Получите ссылку и поделитесь с пользователями' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      {!user && (
        <div className="py-20 bg-primary-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Готовы начать?
            </h2>
            <p className="text-xl mb-8 text-primary-100">
              Создайте ваш первый FAQ прямо сейчас
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
            >
              <Users className="h-5 w-5 mr-2" />
              Зарегистрироваться бесплатно
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;